import styled from '@emotion/styled'
import { FadeInUp } from 'app/components/Common/Animation/FadeInUp'
import { Media } from 'app/theme/media'
import { motion, useAnimation } from 'framer-motion'
import React, { memo, useEffect, useRef, useState } from 'react'
import { useInView } from 'react-intersection-observer'

export interface Props {
  className?: string
  delayChildren?: number
  duration?: number
  tag?: keyof JSX.IntrinsicElements
  text: string
}

export const AnimatedTitle = memo(function AnimatedTitle({
  className,
  delayChildren = 0,
  duration = 2,
  tag: Container = 'div',
  text,
}: Props) {
  if (!text) {
    return null
  }

  const { ref, inView } = useInView({
    threshold: 0.2,
    triggerOnce: true,
  })

  const ctrls = useAnimation()

  const characterAnimation = {
    hidden: {
      y: '120%',
    },
    visible: {
      y: 0,
      transition: {
        duration,
        ease: [0.19, 1.0, 0.22, 1.0],
      },
    },
  }

  const [lines, setLines] = useState([])
  const testRef = useRef(null)

  useEffect(() => {
    const testEl = testRef.current as any
    let line = [] as any
    let output = [] as any

    text.split(' ').forEach((word) => {
      testEl.innerHTML = (testEl.innerHTML + ' ' + word).trim()

      if (testEl.scrollWidth > testEl.clientWidth) {
        testEl.innerHTML = word
        output = output.concat(line.join(' '))
        line = []
      }

      line = line.concat(word)
    })

    output = output.concat(line.join(' '))
    setLines(output)
  }, [text])

  useEffect(() => {
    if (inView) {
      ctrls.start('visible')
    }

    if (!inView) {
      ctrls.start('hidden')
    }
  }, [ctrls, inView])

  return (
    <Container className={className}>
      <Test aria-hidden="true" ref={testRef} tabIndex={-1} />

      <Wrapper
        animate={ctrls}
        initial="hidden"
        ref={ref}
        variants={{
          visible: {
            transition: { delayChildren, staggerChildren: 0.1 },
          },
          hidden: {},
        }}
      >
        <Media lessThan="desktopSmall">
          {(className: any, renderChildren: any) => {
            return renderChildren ? (
              <FadeInUp className={className}>
                <Line>{text}</Line>
              </FadeInUp>
            ) : null
          }}
        </Media>

        <Media greaterThanOrEqual="desktopSmall">
          {(className: any, renderChildren: any) => {
            return renderChildren
              ? lines.map((line, index) => {
                  return (
                    <Mask key={index}>
                      <Line
                        aria-label={text}
                        className={className}
                        tabIndex={Container === 'h2' ? 0 : -1}
                        variants={characterAnimation}
                      >
                        {line}
                      </Line>
                    </Mask>
                  )
                })
              : null
          }}
        </Media>
      </Wrapper>
    </Container>
  )
})

const Test = styled.div`
  display: hidden;
  height: 0;
  overflow-x: scroll;
  white-space: nowrap;

  @media (max-width: 1199px) {
    white-space: normal;
  }
`

const Wrapper = styled(motion.div)`
  overflow: hidden;

  @media (max-width: 1199px) {
    overflow: visible;
  }
`

const Mask = styled.div`
  overflow: hidden;
`

const Line = styled(motion.span)`
  display: block;
  white-space: nowrap;
  will-change: transform;

  @media (max-width: 1199px) {
    white-space: normal;
  }
`
